import { Check, ChevronDown, Target } from 'lucide-react';
import { useIntl } from 'react-intl';
import {
    COURSE_STAGE_VALUES,
    courseStageLabelId,
    type CourseStage,
} from '../model';
import CourseStageDot from './CourseStageDot';
import PickerMenu from './PickerMenu';

type CourseStagePickerProps = {
    stage: CourseStage;
    done: readonly CourseStage[];
    onChange: (change: {
        stage: CourseStage;
        done: readonly CourseStage[];
    }) => void;
    disabled?: boolean;
};

/**
 * Sets the patient's course stage: where the course is, and nothing else.
 * Marking a stage finished is the strip's job — its pebbles are the checklist
 * — so picking a stage here never claims that the ones it passed were done.
 * Leaving Received is the one exception: the Connector marks it done.
 *
 * The stage is book-keeping: it locks nothing, gates nothing, and is never
 * derived from the patient's datasets. That is the model, not something the
 * menu explains — a clinician reads the stage names and the marks, and copy
 * about what the system does not infer would only be noise.
 */
export default function CourseStagePicker({
    stage,
    done,
    onChange,
    disabled = false,
}: CourseStagePickerProps) {
    const intl = useIntl();

    const label = (value: CourseStage) =>
        intl.formatMessage({ id: courseStageLabelId(value) });

    return (
        <PickerMenu
            ariaLabel={intl.formatMessage(
                { id: 'patientList.stage.controlLabel' },
                { stage: label(stage) }
            )}
            title={intl.formatMessage({ id: 'patientList.stage.change' })}
            disabled={disabled}
            entries={COURSE_STAGE_VALUES.map((value) => {
                const isCurrent = value === stage;
                const isDone = done.includes(value);
                return {
                    key: value,
                    label: label(value),
                    detail: isCurrent
                        ? intl.formatMessage({
                              id: 'patientList.stage.current',
                          })
                        : isDone
                          ? intl.formatMessage({ id: 'patientList.stage.done' })
                          : undefined,
                    icon: isCurrent ? (
                        <Target />
                    ) : isDone ? (
                        <Check />
                    ) : undefined,
                    selected: isCurrent,
                    onPick: () => onChange({ stage: value, done }),
                };
            })}
        >
            <CourseStageDot stage={stage} />
            {label(stage)}
            <ChevronDown size={11} />
        </PickerMenu>
    );
}
